import { useEffect, useMemo } from "react";
import { useTheme } from "../ThemeProvider";
import YesNoField from "./YesNoField";
import { opcionesEstadoFicha } from "../../utils/fichaCaptacionAux";
import api from "../../services/api";
import usePermiso from "../../hooks/usePermiso";

import { usePropiedadUi } from "../../hooks/usePropiedadUi";

const M2_DECL_INFO_START = "--- Declaración m² construidos ---";
const M2_DECL_INFO_END = "--- Fin declaración m² ---";

function mergeInfoVisitaDeclaracionForm(infoActual, formLike) {
  const decl = formLike.m2_declarados_escritura;
  const lineas = [];
  if (decl === true || decl === false) {
    lineas.push(M2_DECL_INFO_START);
    lineas.push(`¿m² construidos declarados en escritura?: ${decl ? "Sí" : "No"}`);
    if (decl === false) {
      if (formLike.m2_se_pueden_declarar === true || formLike.m2_se_pueden_declarar === false) {
        lineas.push(`¿Se pueden declarar?: ${formLike.m2_se_pueden_declarar ? "Sí" : "No"}`);
      }
      if (formLike.m2_se_pueden_declarar === true) {
        const quien = String(formLike.m2_declaracion_quien_paga || "").trim();
        if (quien) lineas.push(`¿Quién paga la declaración?: ${quien}`);
      }
    }
    lineas.push(M2_DECL_INFO_END);
  }
  const actual = String(infoActual || "");
  const re = new RegExp(
    `${M2_DECL_INFO_START.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}[\\s\\S]*?${M2_DECL_INFO_END.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\n?`,
    "g",
  );
  const sin = actual.replace(re, "").trim();
  const bloque = lineas.join("\n");
  return bloque ? (sin ? `${sin}\n\n${bloque}` : bloque) : sin;
}

function M2Field({ label, name, form, cambiar, cambiarDirecto, inputStyle, labelStyle, obsColor, disabled = false, tv = (x) => x }) {
  const full = { ...inputStyle, width: "100%", boxSizing: "border-box" };
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>{tv(label)}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <input type="number" step="1" name={name}
          id={`campo-${name}`}
          defaultValue={form[name] ? Math.round(form[name]) : ""}
          onBlur={e => cambiarDirecto(name, e.target.value)}
          disabled={disabled}
          title={disabled ? "No tienes permiso para modificar" : undefined}
          style={{ ...full, flex: 1, minWidth: 0, MozAppearance: "textfield", opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }} />
        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600, flexShrink: 0 }}>m²</span>
      </div>
      <input type="text" name={name + "_obs"} value={form[name + "_obs"] || ""} onChange={cambiar}
        placeholder={tv("Obs.")} tabIndex={-1}
        disabled={disabled}
        style={{ ...full, fontSize: 11, padding: "5px 10px", marginTop: 4, color: obsColor, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }} />
    </div>
  );
}

function CountField({ label, name, form, cambiar, inputStyle, labelStyle, obsColor, value, obsValue, tv = (x) => x }) {
  const full = { ...inputStyle, width: "100%", boxSizing: "border-box" };
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>{tv(label)}</label>
      <input type="number" name={name} value={value ?? form[name] ?? ""} onChange={cambiar} style={full} min={0} />
      <input type="text" name={`${name}_obs`} value={obsValue ?? form[`${name}_obs`] ?? ""} onChange={cambiar}
        placeholder={tv("Obs.")} tabIndex={-1}
        style={{ ...full, fontSize: 11, padding: "5px 10px", marginTop: 4, color: obsColor }} />
    </div>
  );
}

export default function SeccionGenerales({ form, cambiar, cambiarDirecto, auxiliares, actualizarAuxiliar, tv: tvProp, t: tProp }) {
  const { tv, t } = usePropiedadUi({ tv: tvProp, t: tProp });
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const puedeVerEstado = puede("estado", "ver");
  const puedeModificarEstado = puede("estado", "modificar");
  const puedeVerM2Construidos = puede("m2_construidos", "ver");
  const puedeModificarM2Construidos = puede("m2_construidos", "modificar");
  const puedeVerM2Utiles = puede("m2_utiles", "ver");
  const puedeModificarM2Utiles = puede("m2_utiles", "modificar");
  const puedeVerM2Terrazas = puede("m2_terrazas", "ver");
  const puedeModificarM2Terrazas = puede("m2_terrazas", "modificar");
  const puedeVerM2Parcela = puede("m2_parcela", "ver");
  const puedeModificarM2Parcela = puede("m2_parcela", "modificar");
  const puedeVerAnioConstruccion = puede("anio_construccion", "ver");
  const puedeModificarAnioConstruccion = puede("anio_construccion", "modificar");
  const puedeVerOrientacion = puede("orientacion", "ver");
  const puedeModificarOrientacion = puede("orientacion", "modificar");
  const puedeVerCalificacionEnergetica = puede("calificacion_energetica", "ver");
  const puedeModificarCalificacionEnergetica = puede("calificacion_energetica", "modificar");
  const puedeVerM2Jardin = puede("m2_jardin", "ver");
  const puedeModificarM2Jardin = puede("m2_jardin", "modificar");
  const puedeVerNumTerrazas = puede("num_terrazas", "ver");
  const puedeModificarNumTerrazas = puede("num_terrazas", "modificar");
  const puedeVerNumBalcones = puede("num_balcones", "ver");
  const puedeModificarNumBalcones = puede("num_balcones", "modificar");
  const puedeVerNumPatio = puede("num_patio", "ver");
  const puedeModificarNumPatio = puede("num_patio", "modificar");
  const puedeVerAnoReforma = puede("ano_reforma", "ver");
  const puedeModificarAnoReforma = puede("ano_reforma", "modificar");
  const puedeVerPlanos = puede("planos", "ver");
  const puedeModificarPlanos = puede("planos", "modificar");
  const puedeVerInspeccionIte = puede("inspeccion_ite", "ver");
  const puedeModificarInspeccionIte = puede("inspeccion_ite", "modificar");
  const puedeVerExterior = puede("exterior", "ver");
  const puedeModificarExterior = puede("exterior", "modificar");
  const puedeVerCedulaHabitabilidad = puede("cedula_habitabilidad", "ver");
  const puedeModificarCedulaHabitabilidad = puede("cedula_habitabilidad", "modificar");
  const puedeVerVpo = puede("vpo", "ver");
  const puedeModificarVpo = puede("vpo", "modificar");

  const orientaciones = auxiliares["orientacion"] || [];
  const estadosInmueble = auxiliares["estado-inmueble"] || [];
  const estadosOpciones = useMemo(
    () => opcionesEstadoFicha(estadosInmueble),
    [estadosInmueble],
  );


  // Fallback si el padre aún no cargó estado-inmueble. No depender solo de la identidad
  // de actualizarAuxiliar: si no es estable, setState → re-render → effect → bucle de API.
  useEffect(() => {
    if (!actualizarAuxiliar) return undefined;
    if (estadosInmueble.length > 0) return undefined;
    let cancelado = false;
    (async () => {
      try {
        const res = await api.get("/auxiliares/estado-inmueble");
        if (cancelado) return;
        const data = res.data?.data;
        if (Array.isArray(data) && data.length > 0) {
          actualizarAuxiliar("estado-inmueble", data);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelado = true; };
  }, [actualizarAuxiliar, estadosInmueble.length]);

  const CAL = ["A", "B", "C", "D", "E", "F", "G", "En trámite", "Exento"];

  const syncInfoVisitaDecl = (patch) => {
    const nextForm = { ...form, ...patch };
    const merged = mergeInfoVisitaDeclaracionForm(form.info_visita, nextForm);
    if (merged !== String(form.info_visita || "")) {
      cambiarDirecto("info_visita", merged);
    }
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 6, display: "block",
  };
  const inputStyle = {
    padding: "8px 12px", border: `1px solid ${c.chipBorder}`,
    borderRadius: 8, fontSize: 13, color: c.textPrimary,
    background: isDark ? '#0a0e16' : '#dceeff', width: "100%", outline: "none",
    boxShadow: isDark ? 'inset 0 3px 6px rgba(0,0,0,0.5), inset 0 1px 3px rgba(0,0,0,0.3)' : 'inset 0 3px 6px rgba(100,140,200,0.18), inset 0 1px 3px rgba(80,120,180,0.13)',
  };

  // Chip estilo FichaCliente
  const chipStyle = (activo) => ({
    padding: "4px 10px", fontSize: 11, fontWeight: 500,
    borderRadius: 6,
    border: `1px solid ${activo ? c.chipActiveBorder : c.chipBorder}`,
    background: activo ? c.chipActiveBg : c.chipBg,
    color: activo ? c.chipActiveText : c.chipText,
    cursor: "pointer", transition: "all 0.15s ease",
    userSelect: "none", lineHeight: "1.4",
  });
  const onEnter = (e, activo) => {
    if (!activo) { e.currentTarget.style.background = c.chipHoverBg; e.currentTarget.style.color = c.chipHoverText; e.currentTarget.style.borderColor = c.chipHoverBorder; }
  };
  const onLeave = (e, activo) => {
    e.currentTarget.style.background = activo ? c.chipActiveBg : c.chipBg;
    e.currentTarget.style.color = activo ? c.chipActiveText : c.chipText;
    e.currentTarget.style.borderColor = activo ? c.chipActiveBorder : c.chipBorder;
  };

  // Toggle legacy — sustituido por YesNoField en campos obligatorios
  const yesNo = (name, label, onSide, disabled = false) => (
    <YesNoField
      name={name}
      label={label}
      form={form}
      cambiarDirecto={cambiarDirecto}
      compact
      chipStyle={chipStyle}
      onEnter={onEnter}
      onLeave={onLeave}
      disabled={disabled}
      onChangeSideEffect={onSide}
    />
  );

  

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* SUPERFICIES */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, minmax(0, 1fr))", gap: 10 }}>
        {puedeVerM2Construidos && <M2Field label="Construidos" name="m2_construidos" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={c.textSecondary} disabled={!puedeModificarM2Construidos} tv={tv} />}
        {puedeVerM2Utiles && <M2Field label="Útiles" name="m2_utiles" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={c.textSecondary} disabled={!puedeModificarM2Utiles} tv={tv} />}
        {puedeVerM2Terrazas && <M2Field label="Terrazas" name="m2_terrazas" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={c.textSecondary} disabled={!puedeModificarM2Terrazas} tv={tv} />}
        {puedeVerM2Jardin && <M2Field label="Jardín" name="m2_jardin" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={c.textSecondary} disabled={!puedeModificarM2Jardin} tv={tv} />}
        {puedeVerM2Parcela && <M2Field label="Parcela" name="m2_parcela" form={form} cambiar={cambiar} cambiarDirecto={cambiarDirecto} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={c.textSecondary} disabled={!puedeModificarM2Parcela} tv={tv} />}
        {puedeVerNumTerrazas && (
        <CountField
          label="Nº Terrazas"
          name="num_terrazas"
          form={form}
          cambiar={cambiar}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
          obsColor={c.textSecondary}
          value={form.num_terrazas ?? form.num_terrazas_balcones ?? ""}
          obsValue={form.num_terrazas_obs ?? form.num_terrazas_balcones_obs ?? ""}
          tv={tv}
        />
        )}
        {puedeVerNumBalcones && <CountField label="Nº Balcones" name="num_balcones" form={form} cambiar={cambiar} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={c.textSecondary} tv={tv} />}
        {puedeVerNumPatio && <CountField label="Nº Patios" name="num_patio" form={form} cambiar={cambiar} inputStyle={inputStyle} labelStyle={labelStyle} obsColor={c.textSecondary} tv={tv} />}
      </div>

      {/* Declaración registral m² (Casa de campo) */}
      {(
        (() => {
          const tipos = auxiliares["tipo-inmueble"] || auxiliares.tipo_inmueble || [];
          const tipo = tipos.find((t) => String(t.id) === String(form.tipo_inmueble_id));
          const nom = String(tipo?.nombre || form.tipo_inmueble || "").trim();
          return (
            /^casa\s+de\s+campo$/i.test(nom)
            || form.m2_declarados_escritura === true
            || form.m2_declarados_escritura === false
          );
        })()
      ) && (
        <div style={{
          padding: 12,
          borderRadius: 10,
          border: `1px solid ${c.sectionBorder}`,
          background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary }}>
            {tv("Declaración de m² construidos (escritura)")}
          </div>
          <YesNoField
            name="m2_declarados_escritura"
            label={tv("¿Están los m² construidos declarados en escritura?")}
            form={form}
            cambiarDirecto={(name, val) => {
              const patch = { [name]: val };
              if (val === true) {
                patch.m2_se_pueden_declarar = null;
                patch.m2_declaracion_quien_paga = "";
              }
              Object.entries(patch).forEach(([k, v]) => cambiarDirecto(k, v));
              syncInfoVisitaDecl(patch);
            }}
            labelAbove
            disabled={!puedeModificarM2Construidos}
          />
          {form.m2_declarados_escritura === false && (
            <YesNoField
              name="m2_se_pueden_declarar"
              label={tv("¿Se pueden declarar?")}
              form={form}
              cambiarDirecto={(name, val) => {
                const patch = { [name]: val };
                if (val !== true) patch.m2_declaracion_quien_paga = "";
                Object.entries(patch).forEach(([k, v]) => cambiarDirecto(k, v));
                syncInfoVisitaDecl(patch);
              }}
              labelAbove
              disabled={!puedeModificarM2Construidos}
            />
          )}
          {form.m2_declarados_escritura === false && form.m2_se_pueden_declarar === true && (
            <div>
              <label style={labelStyle}>{tv("¿Quién paga la declaración?")}</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {["Propietario", "Comprador", "Compartido", "A negociar"].map((op) => {
                  const sel = String(form.m2_declaracion_quien_paga || "") === op;
                  return (
                    <button
                      key={op}
                      type="button"
                      disabled={!puedeModificarM2Construidos}
                      onClick={() => {
                        const v = sel ? "" : op;
                        cambiarDirecto("m2_declaracion_quien_paga", v);
                        syncInfoVisitaDecl({ m2_declaracion_quien_paga: v });
                      }}
                      style={{
                        padding: "5px 10px",
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: 6,
                        border: `1px solid ${sel ? "#2563eb" : c.chipBorder}`,
                        background: sel ? "#2563eb" : (isDark ? "#0a0e16" : "#fff"),
                        color: sel ? "#fff" : c.textPrimary,
                        cursor: puedeModificarM2Construidos ? "pointer" : "not-allowed",
                        opacity: puedeModificarM2Construidos ? 1 : 0.6,
                      }}
                    >
                      {op}
                    </button>
                  );
                })}
              </div>
              <input
                type="text"
                name="m2_declaracion_quien_paga"
                value={form.m2_declaracion_quien_paga || ""}
                onChange={(e) => {
                  cambiar(e);
                  syncInfoVisitaDecl({ m2_declaracion_quien_paga: e.target.value });
                }}
                onBlur={(e) => {
                  syncInfoVisitaDecl({ m2_declaracion_quien_paga: e.target.value });
                }}
                placeholder={tv("O texto libre…")}
                disabled={!puedeModificarM2Construidos}
                style={{
                  ...inputStyle,
                  opacity: puedeModificarM2Construidos ? 1 : 0.6,
                  cursor: puedeModificarM2Construidos ? "text" : "not-allowed",
                }}
              />
            </div>
          )}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${c.sectionBorder}` }} />

      {/* AÑO / ESTADO */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        {puedeVerAnioConstruccion && (
        <div style={{ minWidth: 120 }}>
          <label style={labelStyle}>{tv("Año construcción")}</label>
          <input
            type="number"
            name="ano_construccion"
            value={form.ano_construccion || ""}
            onChange={cambiar}
            placeholder="2005"
            disabled={!puedeModificarAnioConstruccion}
            title={!puedeModificarAnioConstruccion ? "No tienes permiso para modificar" : undefined}
            style={{ ...inputStyle, opacity: puedeModificarAnioConstruccion ? 1 : 0.6, cursor: puedeModificarAnioConstruccion ? "text" : "not-allowed" }}
          />
        </div>
        )}
        {puedeVerAnoReforma && (
        <div style={{ minWidth: 120 }}>
          <label style={labelStyle}>{tv("Año reforma")}</label>
          <input type="number" name="ano_reforma" value={form.ano_reforma || ""} onChange={cambiar} placeholder="2020"
            disabled={!puedeModificarAnoReforma}
            title={!puedeModificarAnoReforma ? "No tienes permiso para modificar" : undefined}
            style={{ ...inputStyle, opacity: puedeModificarAnoReforma ? 1 : 0.6, cursor: puedeModificarAnoReforma ? "text" : "not-allowed" }} />
        </div>
        )}
        {puedeVerEstado && (
          <div style={{ flex: "1 1 100%" }}>
            <label style={labelStyle}>{t("estado_inmueble_nombre", "Estado")}</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {estadosOpciones.map(({ nombre, id }) => {
                const sel = id != null && String(form.estado_inmueble_id) === String(id);
                return (
                  <button
                    key={id || nombre}
                    type="button"
                    disabled={!puedeModificarEstado}
                    title={!puedeModificarEstado ? "No tienes permiso para modificar el estado del inmueble" : undefined}
                    onClick={() => {
                      if (!puedeModificarEstado || id == null) return;
                      cambiarDirecto("estado_inmueble_id", sel ? "" : id);
                    }}
                    style={{
                      ...chipStyle(sel),
                      cursor: puedeModificarEstado ? "pointer" : "not-allowed",
                      opacity: puedeModificarEstado ? 1 : 0.6,
                    }}
                    onMouseEnter={(e) => puedeModificarEstado && onEnter(e, sel)}
                    onMouseLeave={(e) => puedeModificarEstado && onLeave(e, sel)}
                  >
                    {nombre}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ORIENTACIÓN — chips */}
      {puedeVerOrientacion && orientaciones.length > 0 && (
        <div>
          <label style={labelStyle}>{tv("Orientación")}</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {orientaciones.map(o => {
              const activo = String(form.orientacion_id) === String(o.id);
              return (
                <button key={o.id} type="button"
                  onClick={() => puedeModificarOrientacion && cambiarDirecto("orientacion_id", activo ? "" : o.id)}
                  disabled={!puedeModificarOrientacion}
                  title={!puedeModificarOrientacion ? "No tienes permiso para modificar" : undefined}
                  style={{ ...chipStyle(activo), cursor: puedeModificarOrientacion ? "pointer" : "not-allowed", opacity: puedeModificarOrientacion ? 1 : 0.6 }}
                  onMouseEnter={e => puedeModificarOrientacion && onEnter(e, activo)}
                  onMouseLeave={e => puedeModificarOrientacion && onLeave(e, activo)}
                >
                  {o.nombre}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* CALIFICACIÓN ENERGÉTICA — chips (mismo estilo azul que orientación) */}
      {puedeVerCalificacionEnergetica && (
      <div>
        <label style={labelStyle}>{tv("Calificación energética")}</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {CAL.map((valor) => {
            const activo = form.cal_energetica === valor;
            const esLetra = valor.length === 1;
            return (
              <button key={valor} type="button"
                onClick={() => puedeModificarCalificacionEnergetica && cambiarDirecto("cal_energetica", activo ? "" : valor)}
                disabled={!puedeModificarCalificacionEnergetica}
                title={!puedeModificarCalificacionEnergetica ? "No tienes permiso para modificar" : undefined}
                style={{
                  ...chipStyle(activo),
                  width: esLetra ? 34 : "auto",
                  fontSize: esLetra ? 13 : 11,
                  fontWeight: esLetra ? 700 : 500,
                  textAlign: "center",
                  cursor: puedeModificarCalificacionEnergetica ? "pointer" : "not-allowed",
                  opacity: puedeModificarCalificacionEnergetica ? 1 : 0.6,
                }}
                onMouseEnter={e => puedeModificarCalificacionEnergetica && onEnter(e, activo)}
                onMouseLeave={e => puedeModificarCalificacionEnergetica && onLeave(e, activo)}
              >
                {tv(valor)}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* SÍ / NO explícito */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
        {puedeVerPlanos && yesNo("planos", "Planos", undefined, !puedeModificarPlanos)}
        {puedeVerInspeccionIte && yesNo("inspeccion_ite", "Inspección ITE", undefined, !puedeModificarInspeccionIte)}
        {puedeVerExterior && yesNo("exterior", "Exterior", undefined, !puedeModificarExterior)}
        {puedeVerCedulaHabitabilidad && yesNo("cedula_habitabilidad", "Cédula habitabilidad", undefined, !puedeModificarCedulaHabitabilidad)}
        {puedeVerVpo && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          {yesNo("vpo", "V.P.O.", (v) => { if (v !== true) cambiarDirecto("vpo_ano_fin", ""); }, !puedeModificarVpo)}
          {form.vpo === true && (
            <div>
              <label style={labelStyle}>{tv("Año fin VPO")}</label>
              <input type="number" name="vpo_ano_fin" value={form.vpo_ano_fin || ""} onChange={cambiar}
                placeholder="2030"
                disabled={!puedeModificarVpo}
                title={!puedeModificarVpo ? "No tienes permiso para modificar" : undefined}
                style={{
                  ...inputStyle,
                  width: 88,
                  padding: "4px 10px",
                  fontSize: 11,
                  lineHeight: "1.4",
                  minHeight: 28,
                  boxSizing: "border-box",
                  borderRadius: 6,
                  opacity: puedeModificarVpo ? 1 : 0.6,
                  cursor: puedeModificarVpo ? "text" : "not-allowed",
                }} />
            </div>
          )}
        </div>
        )}
      </div>

    </div>
  );
}
